import { useState } from 'react';
import type { ReactElement } from 'react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';

import { useInitializeMasterFunnelMutation } from '../api/master.api';
import { ProjectCapabilities } from '../api/types';
import FeatureToggleGroup from './FeatureToggleGroup';

interface Props {
  capabilities: ProjectCapabilities;
  error: string | null;
  hasFunnel?: boolean | null;
  isDirty: boolean;
  isLoading: boolean;
  projectId: number;
  onCapabilitiesChange: (next: ProjectCapabilities) => void;
  onSave: () => Promise<void>;
}

const MasterCapabilitiesSection = ({
  capabilities,
  error,
  hasFunnel,
  isDirty,
  isLoading,
  projectId,
  onCapabilitiesChange,
  onSave,
}: Props): ReactElement => {
  const [initializeFunnel, { isLoading: isFunnelLoading }] = useInitializeMasterFunnelMutation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [activationSucceeded, setActivationSucceeded] = useState(false);
  const [funnelError, setFunnelError] = useState<string | null>(null);
  const isFunnelEnabled = Boolean(hasFunnel) || activationSucceeded;

  const handleEnableFunnel = async (): Promise<void> => {
    setFunnelError(null);
    try {
      await initializeFunnel({ projectId }).unwrap();
      setActivationSucceeded(true);
    } catch {
      setFunnelError('No se pudo activar el funnel.');
    }
  };

  return (
    <section className="space-y-4 rounded-md border border-border p-6">
      <div className="flex items-start justify-end gap-4">
        <Button
          type="button"
          onClick={() => {
            void onSave();
          }}
          disabled={!isDirty || isLoading}
        >
          {isLoading ? 'Guardando...' : 'Guardar'}
        </Button>
      </div>

      <FeatureToggleGroup
        value={capabilities}
        onChange={(next) => onCapabilitiesChange({ ...capabilities, ...next })}
      />

      <div className="flex items-start justify-between gap-3 rounded-md border border-border p-4">
        <div>
          <Label htmlFor="assignment-agent-toggle">Asignación de clientes a ejecutivos/as</Label>
          <p className="text-xs text-text-muted">Mantiene la capacidad de asignación automática a nivel master.</p>
        </div>
        <Switch
          id="assignment-agent-toggle"
          checked={capabilities.assignmentAgentEnabled}
          onCheckedChange={(checked) => {
            onCapabilitiesChange({ ...capabilities, assignmentAgentEnabled: checked });
          }}
        />
      </div>

      <div className="flex items-center justify-between gap-3 rounded-md border border-border p-4">
        <div>
          <Label>Vista funnel</Label>
        </div>
        <Button
          type="button"
          onClick={() => setIsDialogOpen(true)}
          disabled={isFunnelEnabled || isFunnelLoading}
          variant={isFunnelEnabled ? 'secondary' : 'default'}
        >
          {isFunnelEnabled ? 'Vista funnel habilitada' : 'Habilitar vista funnel'}
        </Button>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{activationSucceeded ? 'Vista funnel habilitada' : '¿Activar la vista funnel?'}</DialogTitle>
            <DialogDescription>
              {activationSucceeded
                ? 'La vista funnel quedó habilitada para esta inmobiliaria.'
                : 'Se habilitará la vista funnel para esta inmobiliaria.'}
            </DialogDescription>
          </DialogHeader>

          {funnelError && (
            <div className="bg-error-light border border-error-light rounded-lg px-4 py-3 text-sm text-error">
              {funnelError}
            </div>
          )}

          <DialogFooter>
            {activationSucceeded ? (
              <Button type="button" onClick={() => setIsDialogOpen(false)}>
                Cerrar
              </Button>
            ) : (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                  disabled={isFunnelLoading}
                >
                  Cancelar
                </Button>
                <Button
                  type="button"
                  onClick={() => {
                    void handleEnableFunnel();
                  }}
                  disabled={isFunnelLoading}
                >
                  {isFunnelLoading ? 'Habilitando...' : 'Activar'}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {error && (
        <p className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">{error}</p>
      )}
    </section>
  );
};

export default MasterCapabilitiesSection;
